import {
  propertyTypeOptions,
  transactionStatusOptions,
} from "../../app/common/categoryData/categoryOptions";

export const conditionalBasedOnOptions = [
  { key: "document", value: "document", text: "Signed Document" },
  { key: "transactionStatus", value: "transactionStatus", text: "Transaction Status" },
  { key: "propertyType", value: "propertyType", text: "Property Type" },
  {
    key: "agentIsMortgageBroker",
    value: "agentIsMortgageBroker",
    text: "Is Agent a Mortgage Broker?",
  },
  {
    key: "yearBuiltBefore1978",
    value: "yearBuiltBefore1978",
    text: "Property Built Before 1978",
  },
  {
    key: "hasTransactionCoordinator",
    value: "hasTransactionCoordinator",
    text: "Has Transaction Coordinator",
  },
  { key: "hasAssistant", value: "hasAssistant", text: "Has Assistant" },
  { key: "date", value: "date", text: "Date" },
];

const conditionsWithoutValues = [
  "agentismortgagebroker",
  "yearbuiltbefore1978",
  "hastransactioncoordinator",
  "hasassistant",
];

function normalizeConditionType(conditionalBasedOn = "") {
  return conditionalBasedOn.toLowerCase();
}

function isTruthy(value) {
  return value === true || value === "true" || value === "Yes" || value === "yes";
}

function getOptionText(options, value) {
  return options.find((option) => option.value === value)?.text || value;
}

export function conditionNeedsValue(conditionalBasedOn) {
  const conditionType = normalizeConditionType(conditionalBasedOn);
  return !!conditionType && !conditionsWithoutValues.includes(conditionType);
}

export function getConditionalValueLabel(conditionalBasedOn) {
  const conditionType = normalizeConditionType(conditionalBasedOn);
  if (conditionType === "document") return "Document Name";
  if (conditionType === "transactionstatus") return "Transaction Status";
  if (conditionType === "propertytype") return "Property Type";
  if (conditionType === "date") return "Date";
  return "Condition";
}

export function getComplianceItemOptions(categories = [], currentItem = null) {
  return categories.flatMap((category, categoryIndex) =>
    (category.items || [])
      .filter((categoryItem) => categoryItem?.name && categoryItem !== currentItem)
      .map((categoryItem, itemIndex) => ({
        key: `${categoryIndex}-${itemIndex}-${categoryItem.name}`,
        value: categoryItem.name,
        text: categoryItem.name,
      }))
  );
}

export function getConditionalValueOptions(
  conditionalBasedOn,
  categories,
  currentItem,
  dateOptions = []
) {
  const conditionType = normalizeConditionType(conditionalBasedOn);
  if (conditionType === "document") return getComplianceItemOptions(categories, currentItem);
  if (conditionType === "transactionstatus") return transactionStatusOptions;
  if (conditionType === "propertytype") {
    return propertyTypeOptions.filter((option) => option.value !== "All");
  }
  if (conditionType === "date") return dateOptions;
  return [];
}

export function getConditionalDisplayText(item, { dateOptions = [] } = {}) {
  const conditionType = normalizeConditionType(item?.conditionalBasedOn);
  const value = item?.conditionalValue;

  if (!conditionType) return "";
  if (conditionType === "document" && value) return `Document signed: ${value}`;
  if (conditionType === "transactionstatus" && value) {
    return `Transaction Status = ${getOptionText(transactionStatusOptions, value)}`;
  }
  if (conditionType === "propertytype" && value) {
    return `Property Type = ${getOptionText(propertyTypeOptions, value)}`;
  }
  if (conditionType === "date" && value) {
    return `Date = ${getOptionText(dateOptions, value)}`;
  }
  if (conditionType === "agentismortgagebroker") return "Is Agent a Mortgage Broker?";
  if (conditionType === "yearbuiltbefore1978") return "Year Built < 1978";
  if (conditionType === "hastransactioncoordinator") return "Has Transaction Coordinator";
  if (conditionType === "hasassistant") return "Has Assistant";

  return getOptionText(conditionalBasedOnOptions, item?.conditionalBasedOn) || "";
}

export function getConditionalRequirementInfo(item, allItems, transaction) {
  const templateRequirement = item.templateRequirement || item.requirement;

  if (templateRequirement !== "conditional" || !item.conditionalBasedOn) return null;

  const conditionType = normalizeConditionType(item.conditionalBasedOn);
  const needsValue = conditionNeedsValue(item.conditionalBasedOn);
  const conditionText = getConditionalDisplayText(item);

  if (needsValue && !item.conditionalValue) {
    return conditionText ? { conditionalConditionText: conditionText } : null;
  }

  let conditionIsMet = false;

  if (conditionType === "document") {
    conditionIsMet = allItems.some(
      (complianceItem) =>
        complianceItem.name === item.conditionalValue &&
        complianceItem.documents &&
        complianceItem.documents.length > 0
    );
  } else if (conditionType === "transactionstatus") {
    conditionIsMet =
      (transaction?.status || transaction?.transactionStatus) === item.conditionalValue;
  } else if (conditionType === "propertytype") {
    conditionIsMet = transaction?.propertyType === item.conditionalValue;
  } else if (conditionType === "agentismortgagebroker") {
    conditionIsMet = isTruthy(transaction?.agentProfile?.isMortgageBroker);
  } else if (conditionType === "yearbuiltbefore1978") {
    const yearBuilt = parseInt(transaction?.propertyDetails?.yearBuilt, 10);
    conditionIsMet = Number.isFinite(yearBuilt) && yearBuilt < 1978;
  } else if (conditionType === "hastransactioncoordinator") {
    conditionIsMet = isTruthy(transaction?.hasTc) || isTruthy(transaction?.hasAgentAsTc);
  } else if (conditionType === "hasassistant") {
    conditionIsMet = isTruthy(transaction?.hasAssistant);
  }

  if (!conditionIsMet) {
    return conditionText ? { conditionalConditionText: conditionText } : null;
  }

  return {
    effectiveRequirement: "required",
    conditionalConditionText: conditionText,
    conditionalRequirementMessage: `Required because: ${conditionText}`,
  };
}